import React, { useEffect, useRef, useState } from "react"
import Timer from "../Auction/Timer"
import BankArea from "./BankArea"
import DiceArea from "./DiceArea"
import GameButtons from "./GameButtons"
import PlayersList from "./PlayersList"
import ActiveTradesListPanel from "./ActiveTradesListPanel"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState, setModalData } from "@/store/globalSlice"
import { socket } from "@/utils/utils"
import { leaveGame } from "@/utils/leaveGame"
import { useShouldHideRollButton } from "@/Hooks/useShouldHideRollButton"
import { evaluateRequiredRollValues } from "@/utils/animationHelpers"
import { getCellByOrderAndMapId } from "@/utils/gameHelpers"
import { XMarkIcon } from "@heroicons/react/24/solid"
import classNames from "classnames"
import sidebarBackground from "@/images/backgrounds/sidebar-background.webp"
import GameControlButton from "@/Components/GameControlButton"
import { addMoneyAnimation } from "@/store/globalSlice"
import { useNavigate } from "react-router-dom"
import BackgroundMusicToggle from "@/Components/BackgroundMusicToggle"
import FullscreenToggle from "@/Components/FullscreenToggle"
import { allChallenges } from "@mono/allChallenges"
import { featureFlags, isDemoMode } from "@mono/constants"

interface SidebarProps {}

const Sidebar: React.FC<SidebarProps> = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const myGame = useAppSelector((state) => state.global.myGame)
  const challengeId = useAppSelector(
    (state) => state.global.myGame?.challengeId
  )
  const challenge = challengeId
    ? allChallenges.find((c) => c.id === challengeId)
    : undefined
  // const timerValue = useAppSelector((state) => state.global.timerValue)
  // const isTimerPaused = useAppSelector((state) => state.global.isTimerPaused)

  const ref = useRef<HTMLDivElement>(null)

  const situationPlayers = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const isGamePaused = useAppSelector((state) => state.global.isGamePaused)

  // Pause is only offered in single-human games (everyone else is a bot).
  const humanPlayerCount = (situationPlayers || []).filter((p) => !p.bot).length
  const canPauseGame = humanPlayerCount === 1

  const handleTogglePause = () => {
    const nextPaused = !isGamePaused
    dispatch(setState({ isGamePaused: nextPaused }))
    socket.emit("game.setTurnTimerPaused", myGame?.id || "", nextPaused)
  }

  const handleOpenSettings = () => {
    dispatch(setModalData({ settings: { visible: true } }))
  }

  const handleLeaveGame = () => {
    if (featureFlags.confirmBeforeLeavingGame) {
      dispatch(setModalData({ leaveGameConfirm: { visible: true } }))
      return
    }
    navigate("/lobby")
    leaveGame(myGame, dispatch)
  }
  console.log("Re rendering sidebar")
  return (
    <div
      className={classNames(
        // Height off the playable box, width off what the board leaves behind.
        // The board between the two columns is a full 100vh square — it runs
        // edge to edge over the top and bottom insets on purpose (Board.tsx) —
        // so what is left for each column is half of (--play-w - 100vh). The
        // root's safe-area padding already holds this column clear of the
        // screen edge, and everything positioned against it (the settings
        // cluster below) comes along for free.
        "flex flex-col relative h-[var(--play-h)] bg-cover justify-between w-[calc((var(--play-w)-100vh)/2)]  relative pl-4"

        // { "w-[calc(100vw-120vh)]": state.isMobile }
      )}
      // style={{ backgroundImage: `url(${sidebarBackground})` }}
    >
      {/* Leave button */}

      <PlayersList
        situationPlayers={situationPlayers || []}
        sidebar="right"
      ></PlayersList>

      {/* Fixed-height bottom slot, mirrored in SidebarLeft.tsx, so the players
          list above gets the same box on both sides and the two lists stay
          aligned. The breathing room between the lowest player card and the top
          of this panel is the list's own bottom margin now (see PlayersList),
          which both columns give up together — so the panel is free to use the
          whole slot and the two lists still measure against the same box. */}
      <div className="flex flex-col justify-end gap-2 h-[calc(var(--play-h)*0.3)] shrink-0">
        <ActiveTradesListPanel />
      </div>

      <div className="absolute top-6 right-6 flex items-stretch gap-3">
        <div className="w-[3rem] shrink-0 self-stretch">
          <FullscreenToggle
            testId="game.fullscreen-toggle"
            className="!h-full !w-full"
          />
        </div>
        <div className="w-[3rem] shrink-0 self-stretch">
          <BackgroundMusicToggle
            testId="game.background-music-toggle"
            className="!h-full !w-full"
          />
        </div>
        <GameControlButton
          accent="violet"
          onClick={handleOpenSettings}
          className="!px-3.5"
          data-testid="game.settings-button"
          aria-label="Settings"
        >
          <i className="fa-solid fa-gear fa-lg mobile:!text-[13px]"></i>
        </GameControlButton>
        {canPauseGame && (
          <GameControlButton
            accent="amber"
            onClick={handleTogglePause}
            className="!px-3.5"
            data-testid="game.pause-button"
            aria-label={isGamePaused ? "Resume" : "Pause"}
            title={isGamePaused ? "Resume" : "Pause"}
          >
            <i
              className={classNames(
                "fa-solid fa-lg mobile:!text-[13px]",
                isGamePaused ? "fa-play" : "fa-pause"
              )}
            ></i>
          </GameControlButton>
        )}
        <GameControlButton
          accent="rose"
          onClick={handleLeaveGame}
          aria-label="Leave game"
        >
          <i className="fa-solid fa-sign-out-alt fa-lg mobile:!text-[13px]"></i>
          <span className="font-poppins text-lg">Leave</span>
        </GameControlButton>
      </div>
      {challenge ? (
        <div
          className={classNames(
            "fixed right-[calc(1.5rem+var(--safe-right))] z-50 pointer-events-none",
            {
              // Sit above the "Unlock Full Version" button when it's present in demo mode.
              "bottom-[calc(7rem+var(--safe-bottom))]": isDemoMode,
              "bottom-[calc(3rem+var(--safe-bottom))]": !isDemoMode,
            }
          )}
        >
          <div className="max-w-[16rem] rounded-xl border border-amber-400/30 bg-black/70 px-4 py-2.5 backdrop-blur-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/70">
              Challenge
            </p>
            <p className="text-white font-poppins font-medium text-lg leading-tight">
              {challenge.name}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default React.memo(Sidebar)
